import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'

/*
 * Copy rules (feedback memory `fairmeadow_messaging_tone`): institutional, not
 * contrarian; describe the mechanism, not the virtue; no city or deal size in
 * the headline — geography lives in body copy and the portfolio table.
 *
 * The portfolio table states only facts that do not go stale: acquisition date,
 * price, unit count. Occupancy moves with every turnover, so it lives on the
 * track-record page next to the date it was measured, never here undated.
 */

const portfolio = [
  {
    property: 'West Boulevard duplex',
    location: 'Cleveland, Ohio',
    units: '2',
    layout: '3 bed / 1 bath each',
    acquired: 'April 2025',
    price: '$170,000',
  },
]

const practices = [
  {
    title: 'Underwriting',
    body: 'Every acquisition is modeled against higher vacancy, rising rates, and deferred maintenance before an offer is made. A property that does not hold up in the downside case is declined.',
  },
  {
    title: 'Reporting',
    body: 'Investors receive a quarterly report for each property: income and expenses, occupancy, capital work completed, and conditions in the local market.',
  },
  {
    title: 'Management',
    body: 'Leasing, rent collection, and maintenance are handled by an established third-party property manager. The firm oversees budgets, capital projects, and performance.',
  },
  {
    title: 'Hold period',
    body: 'Properties are bought to be held for five to ten years or longer. Returns are underwritten from rental income, not from an assumed sale price.',
  },
]

const steps = [
  {
    title: 'Join the investor list',
    body: 'You are notified when a new opportunity is available, along with occasional market updates.',
  },
  {
    title: 'Review the offering',
    body: 'Each opportunity comes with the underwriting model, comparable sales and rents, the downside case, and the risks we see.',
  },
  {
    title: 'Invest and receive reports',
    body: 'The firm handles acquisition, renovation, leasing, and oversight. You receive quarterly reports and distributions.',
  },
]

export default function HomePage() {
  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-5 pt-16 pb-12 md:px-8 md:pt-24 md:pb-16">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <h1 className="text-navy text-[clamp(2.6rem,4.6vw+1rem,5.25rem)] leading-[1.04] font-medium tracking-[-0.02em] lg:col-span-8">
            Direct real estate ownership, professionally managed.
          </h1>
          <div className="lg:col-span-4 lg:pb-3">
            <p className="text-muted-foreground max-w-md text-[1.075rem] leading-relaxed">
              Fairmeadow Capital acquires and operates residential multifamily property on behalf of investors who want
              the income of real estate without running it themselves.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/contact">Join the investor list</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/strategy">Read the strategy</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The one image: the city the portfolio is in, at full width, no overlay. */}
      <figure className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="bg-muted relative aspect-[4/3] overflow-hidden sm:aspect-[21/9]">
          <img src="/hero-poster.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
        </div>
        <figcaption className="text-muted-foreground mt-3 text-sm">
          Downtown Cleveland and the Flats. The current portfolio is on the city&rsquo;s west side.
        </figcaption>
      </figure>

      {/* Portfolio */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="text-fluid-h2 text-navy">Portfolio</h2>
          <Link to="/track-record" className="prose-link text-[0.95rem]">
            Full financials for each property
          </Link>
        </div>

        {/* Table on wide screens; the same rows as stacked records on phones. */}
        <table className="hidden w-full border-collapse text-left md:table">
          <thead>
            <tr className="border-navy text-muted-foreground border-b text-sm">
              <th scope="col" className="py-3 pr-6 font-medium">
                Property
              </th>
              <th scope="col" className="py-3 pr-6 font-medium">
                Location
              </th>
              <th scope="col" className="py-3 pr-6 text-right font-medium">
                Units
              </th>
              <th scope="col" className="py-3 pr-6 font-medium">
                Layout
              </th>
              <th scope="col" className="py-3 pr-6 font-medium">
                Acquired
              </th>
              <th scope="col" className="py-3 text-right font-medium">
                Purchase price
              </th>
            </tr>
          </thead>
          <tbody>
            {portfolio.map((p) => (
              <tr key={p.property} className="border-border border-b text-[1.05rem]">
                <th scope="row" className="text-navy py-5 pr-6 font-serif text-[1.3rem] font-medium">
                  {p.property}
                </th>
                <td className="py-5 pr-6">{p.location}</td>
                <td className="py-5 pr-6 text-right">{p.units}</td>
                <td className="py-5 pr-6">{p.layout}</td>
                <td className="py-5 pr-6">{p.acquired}</td>
                <td className="py-5 text-right">{p.price}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="md:hidden">
          {portfolio.map((p) => (
            <dl key={p.property} className="border-navy tabular border-t">
              <dt className="sr-only">Property</dt>
              <dd className="text-navy py-4 font-serif text-[1.35rem]">{p.property}</dd>
              {(
                [
                  ['Location', p.location],
                  ['Units', p.units],
                  ['Layout', p.layout],
                  ['Acquired', p.acquired],
                  ['Purchase price', p.price],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="border-border flex justify-between gap-4 border-t py-3">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-right">{v}</dd>
                </div>
              ))}
            </dl>
          ))}
        </div>

        <p className="text-muted-foreground mt-6 max-w-2xl text-[0.95rem] leading-relaxed">
          The firm is underwriting its next acquisition. Investor-list members are notified before it closes.
        </p>
      </section>

      {/* How the firm operates */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <h2 className="text-fluid-h2 text-navy">How the firm operates</h2>
            <p className="text-muted-foreground mt-5 max-w-sm leading-relaxed">
              The same four practices apply to every property, whatever its size.
            </p>
          </div>
          <div className="grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:col-span-8">
            {practices.map((p) => (
              <div key={p.title} className="border-navy/25 border-t pt-5">
                <h3 className="text-navy mb-3 text-lg font-semibold">{p.title}</h3>
                <p className="text-foreground/85 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investing — a real sequence, so it is numbered. */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <h2 className="text-fluid-h2 text-navy">Investing with Fairmeadow</h2>
            <Link to="/invest" className="prose-link mt-5 inline-block">
              Deal structure and investment range
            </Link>
          </div>
          <ol className="lg:col-span-8">
            {steps.map((s, i) => (
              <li
                key={s.title}
                className="border-border grid grid-cols-[3rem_1fr] gap-x-4 border-t py-7 last:border-b sm:grid-cols-[4.5rem_1fr]"
              >
                <span className="text-navy font-serif text-[2rem] leading-none" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-navy mb-2 text-lg font-semibold">{s.title}</h3>
                  <p className="text-foreground/85 max-w-xl leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Principal */}
      <section className="border-border border-t">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-3">
            <img
              src="/headshot.webp"
              alt="Garrett John Law"
              width={640}
              height={640}
              loading="lazy"
              className="aspect-square w-40 object-cover grayscale-[15%] lg:w-full lg:max-w-[16rem]"
            />
          </div>
          <div className="lg:col-span-7 lg:col-start-5">
            <h2 className="text-fluid-h3 text-navy">Garrett John Law, founder</h2>
            <p className="text-fluid-body-lg mt-4 max-w-2xl">
              Garrett founded Fairmeadow Capital in 2025. He is a software engineer by profession and is responsible for
              the firm&rsquo;s acquisitions, underwriting, and investor reporting. He has invested his own capital in
              every property the firm owns.
            </p>
            <Link to="/about" className="prose-link mt-6 inline-block">
              More about Garrett and the firm
            </Link>
          </div>
        </div>
      </section>

      {/* Investor list */}
      <section className="bg-muted">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-5 py-16 md:flex-row md:items-center md:justify-between md:px-8 md:py-20">
          <div className="max-w-2xl">
            <h2 className="text-fluid-h2 text-navy">Hear about the next acquisition</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Members of the investor list receive each new opportunity before it closes. Joining carries no obligation.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/contact">Join the investor list</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
